import { createContext, useContext, useState, useEffect } from 'react';
import { apiFetch, clearStoredAuth, getStoredAuth, setStoredAuth } from '../utils/api';

const AuthContext = createContext(null);

const ROLE_MAP = {
  OWNER: 'admin',
  ADMIN: 'admin',
  AGENT: 'agent',
  VIEWER: 'viewer',
  SUPER_ADMIN: 'admin',
  TENANT_ADMIN: 'admin',
  SUPERVISOR: 'agent',
};

const buildAvatar = (name) =>
  name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : '??';

const normalizeUser = (user) => {
  if (!user) return null;
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    avatar: user.avatar || buildAvatar(user.name),
    role: ROLE_MAP[user.role] || 'viewer',
    teamId: user.teamId || null,
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = getStoredAuth();
    if (stored?.user) {
      setUser(stored.user);
      setTenant(stored.tenant || null);
    } else if (stored) {
      clearStoredAuth();
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        json: { email, password },
      });
      const data = response?.data;
      if (!data?.accessToken || !data?.user) {
        return { ok: false, error: 'Resposta inválida da API.' };
      }
      const normalizedUser = normalizeUser(data.user);
      const authPayload = {
        token: data.accessToken,
        user: normalizedUser,
        tenant: data.tenant || null,
      };
      setStoredAuth(authPayload);
      setUser(normalizedUser);
      setTenant(data.tenant || null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || 'Falha no login.' };
    }
  };

  const logout = () => {
    clearStoredAuth();
    setUser(null);
    setTenant(null);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    tenant,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
